import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import ListLayout from '../../Generic/ListLayout';
import DataTable from '../../Generic/DataTable';
import confirmDeleteDialog from '../../Generic/confirmDeleteDialog';
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useDeleteItem from '../../../hooks/useDeleteItem';
import useFetchData from '../../../hooks/useFetchData';
import FormSelectSelection from '../../Generic/FormSelectSelection';
import ToolPassportModal from '../../Generic/ToolPassportModal';
import { createGeneralColumn, createActiveColumn, createActionColumn } from '../../Generic/dataGridColumns';
import AuthContext from "../../../context/AuthProvider";
import { useSetMenu } from "../../../hooks/useSetMenu";

const List = () => {
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useContext(AuthContext);
  const { mainStore, toolPassportStore } = useStore();
  const { t } = useTranslation();
  useSetMenu(mainStore.ToolPassportMenuType, mainStore.ToolPassportMenuNo);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const [hoveredLogo, setHoveredLogo] = useState(null);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    setCursorPosition({ x: e.clientX, y: e.clientY });
  };

  // Modal state for row details
  const [openModal, setOpenModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleOpenModal = (row) => {
    setSelectedRow(row);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedRow(null);
  };

  // Fetch tool passports data
  useFetchData(axiosPrivate, `/ToolPassports`, toolPassportStore);

  // ------------------------------------------------
  // Cascading filter states using FormSelectSelection
  // ------------------------------------------------
  const [panelOptions, setPanelOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  // Load panel options on mount
  useEffect(() => {
    const loadPanels = async () => {
      try {
        const res = await axiosPrivate.get("/Panels/SelectOptions");
        setPanelOptions(res.data);
      } catch (err) {
        console.error("Error loading panels", err);
      }
    };
    loadPanels();
  }, [axiosPrivate]);

  const handlePanelChange = async (option) => {
    setSelectedPanel(option);
    setSelectedCategory(null);
    setCategoryOptions([]);
    setSelectedSubcategory(null);
    setSubcategoryOptions([]);

    if (!option) return;
    try {
      const catRes = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${option.value}`);
      setCategoryOptions(Array.isArray(catRes.data) ? catRes.data : []);
    } catch (error) {
      console.error("Error loading categories", error);
    }
  };

  const handleCategoryChange = async (option) => {
    setSelectedCategory(option);
    setSelectedSubcategory(null);
    setSubcategoryOptions([]);
    if (!option) return;
    try {
      const subRes = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${option.value}`);
      setSubcategoryOptions(Array.isArray(subRes.data) ? subRes.data : []);
    } catch (error) {
      console.error("Error loading subcategories", error);
    }
  };

  const handleSubcategoryChange = (option) => {
    setSelectedSubcategory(option);
  };

  // -------------------------------
  // Combine filtering for the data
  // -------------------------------
  const filteredData = useMemo(() => {
    let data = toJS(toolPassportStore.toolPassport) || [];
    // Filter based on selected panel/category/subcategory
    if (selectedPanel) {
      data = data.filter(item => item.panelName === selectedPanel.label);
    }
    if (selectedCategory) {
      data = data.filter(item => item.categoryName === selectedCategory.label);
    }
    if (selectedSubcategory) {
      data = data.filter(item => item.subcategoryName === selectedSubcategory.label);
    }
    // Apply text search filter
    if (searchQuery) {
      data = data.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return data;
  }, [toolPassportStore.toolPassport, selectedPanel, selectedCategory, selectedSubcategory, searchQuery]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const deleteToolPassport = useDeleteItem(axiosPrivate, '/ToolPassports', toolPassportStore.deleteToolPassport);

  const remove = useCallback((id) => {
    confirmDeleteDialog(t, deleteToolPassport, id);
  }, [deleteToolPassport, t]);

  // Use useMemo to compute columns directly and pass them to DataTable
  const cols = useMemo(() => [
    {
      field: 'logo',
      headerName: t("logo"),
      width: 100,
      headerClassName: 'tableHeaderClassName',
      cellClassName: 'tableCellClassName',
      headerAlign: "center",
      align: "center",
      renderHeader: (params) => (
        <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
      ),
      renderCell: (params) => {
        const logoPath = params.row.logo;
        const imageUrl = `${process.env.REACT_APP_SERVER_URLForLogo}/${logoPath}`;
        const defaultImageUrl = `/assets/brand/default_img.png`;

        return (
          <div
            onMouseEnter={() => setHoveredLogo(logoPath ? imageUrl : defaultImageUrl)}
            onMouseLeave={() => setHoveredLogo(null)}
            onMouseMove={handleMouseMove}
            style={{ position: 'relative' }}
          >
            <img src={logoPath ? imageUrl : defaultImageUrl} alt="logo"
              style={{ width: '50px', height: '50px', objectFit: 'contain' }}
              onError={(e) => { e.target.onerror = null; e.target.src = '/default-logo.png'; }}
            />
          </div>
        );
      }
    },
    createGeneralColumn("name", "toolPassportName", t),
    createGeneralColumn("typeOfToolName", "typeOfTool", t),
    createGeneralColumn("subcategoryName", "subcategory", t),
    createGeneralColumn("categoryName", "category", t),
    createGeneralColumn("panelName", "panel", t),
    createActiveColumn(t),
    {
      field: 'details',
      headerName: t("toolPassport"),
      width: 120,
      headerClassName: 'tableHeaderClassName',
      cellClassName: 'tableCellClassName',
      headerAlign: "center",
      align: "center",
      renderHeader: (params) => (
        <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
      ),
      renderCell: (params) => (
        <div onClick={() => handleOpenModal(params.row)} style={{ cursor: "pointer" }}>
          <i className="bi bi-eye text-danger">{" " + t("viewToolPassport")}</i>
        </div>
      )
    },
    createActionColumn(t, "/ToolPassport/Edit", remove, 80),
  ].filter(Boolean), [t, remove]);

  return (
    <>
      <ListLayout
        title={t("toolPassportListTitle")}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        // searchPlaceholder={t("searchPlaceholder")}
        addButtonLink="/ToolPassport/Add"
        addButtonTitle={t("add")}
      >
        {/* Filter row with cascading dropdowns */}
        <div
          className="filter-row"
          style={{ marginBottom: '1rem', display: 'flex', gap: '1rem' }}
        >
          <FormSelectSelection
            label={t("Panel")}
            options={panelOptions}
            value={selectedPanel}
            onChange={handlePanelChange}
          />
          <FormSelectSelection
            label={t("Category")}
            options={categoryOptions}
            value={selectedCategory}
            onChange={handleCategoryChange}
            disabled={!selectedPanel}
          />
          <FormSelectSelection
            label={t("Subcategory")}
            options={subcategoryOptions}
            value={selectedSubcategory}
            onChange={handleSubcategoryChange}
            disabled={!selectedCategory}
          />
        </div>
        <DataTable
          rows={filteredData}
          columns={cols}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </ListLayout>
      {hoveredLogo && (
        <div
          style={{
            position: 'fixed',
            top: `${cursorPosition.y + 20}px`,
            left: `${cursorPosition.x + 20}px`,
            zIndex: 1000,
            width: '150px',
            height: '150px',
            border: '1px solid #ccc',
            backgroundColor: '#fff',
            padding: '5px',
          }}
        >
          <img
            src={hoveredLogo}
            alt="Zoomed logo"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </div>
      )}
      <ToolPassportModal
        open={openModal}
        onClose={handleCloseModal}
        toolPassport={selectedRow}
      />
    </>
  );
};

export default observer(List);


// import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
// import { toJS } from 'mobx';
// import { observer } from 'mobx-react-lite';
// import { useTranslation } from 'react-i18next';
// import { useStore } from '../../../stores/store';
// import ListLayout from '../../Generic/ListLayout';
// import DataTable from '../../Generic/DataTable';
// import confirmDeleteDialog from '../../Generic/confirmDeleteDialog';
// import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
// import useDeleteItem from '../../../hooks/useDeleteItem';
// import useFilterData from '../../../hooks/useFilterData';
// import useFetchData from '../../../hooks/useFetchData';
// import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
// import { useSetMenu } from "../../../hooks/useSetMenu";
// import { createGeneralColumn, createActiveColumn, createActionColumn } from '../../Generic/dataGridColumns';
// import AuthContext from "../../../context/AuthProvider";

// // Material-UI Dialog components
// import Dialog from '@mui/material/Dialog';
// import DialogTitle from '@mui/material/DialogTitle';
// import DialogContent from '@mui/material/DialogContent';
// import DialogActions from '@mui/material/DialogActions';
// import Button from '@mui/material/Button';
// import IconButton from '@mui/material/IconButton';
// import CloseIcon from '@mui/icons-material/Close';


// const List = () => {
//   const axiosPrivate = useAxiosPrivate();
//   const { auth } = useContext(AuthContext);
//   const { mainStore, toolPassportStore } = useStore();
//   const { t } = useTranslation();
//   const localizedTextsMap = useLocalizedTextsMap();
//   useSetMenu(mainStore.ToolPassportMenuType, mainStore.ToolPassportMenuNo);
//   const [columns, setColumns] = useState([]);
//   const [pageSize, setPageSize] = useState(10);
//   const [searchQuery, setSearchQuery] = useState("");

//   const [hoveredLogo, setHoveredLogo] = useState(null);
//   const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });

//   const handleMouseMove = (e) => {
//     setCursorPosition({ x: e.clientX, y: e.clientY });
//   };

//   // New modal state for showing row details
//   const [openModal, setOpenModal] = useState(false);
//   const [selectedRow, setSelectedRow] = useState(null);

//   const handleOpenModal = (row) => {
//     setSelectedRow(row);
//     setOpenModal(true);
//   };

//   const handleCloseModal = () => {
//     setOpenModal(false);
//     setSelectedRow(null);
//   };

//   useFetchData(axiosPrivate, `/ToolPassports`, toolPassportStore);
//   const filteredData = useFilterData(toJS(toolPassportStore.toolPassport), searchQuery);

//   const handleSearchChange = (event) => {
//     setSearchQuery(event.target.value);
//   };

//   const deleteToolPassport = useDeleteItem(axiosPrivate, '/ToolPassports', toolPassportStore.deleteToolPassport);

//   const remove = useCallback((id) => {
//     confirmDeleteDialog(t, deleteToolPassport, id);
//   }, [deleteToolPassport, t]);

//   const cols = useMemo(() => [
//     {
//       field: 'logo',
//       headerName: t("logo"),
//       width: 100,
//       headerClassName: 'tableHeaderClassName',
//       cellClassName: 'tableCellClassName',
//       headerAlign: "center",
//       align: "center",
//       renderHeader: (params) => (
//         <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
//       ),
//       renderCell: (params) => {
//         const logoPath = params.row.logo;
//         // const imageUrl = `${process.env.REACT_APP_SERVER_URLForLogo}/${logoPath}`;
//         const imageUrl = `${process.env.REACT_APP_SERVER_URLForLogo}/${logoPath}`;
//         const defaultImageUrl = `/assets/brand/default_img.png`;

//         return (
//           <div
//             onMouseEnter={() => setHoveredLogo(logoPath ? imageUrl : defaultImageUrl)}
//             onMouseLeave={() => setHoveredLogo(null)}
//             onMouseMove={handleMouseMove}
//             style={{ position: 'relative' }}
//           >
//             <img src={logoPath ? imageUrl : defaultImageUrl} alt="logo"
//               style={{ width: '50px', height: '50px', objectFit: 'contain' }}
//               onError={(e) => { e.target.onerror = null; e.target.src = '/default-logo.png'; }}
//             />
//           </div>
//         );
//       }
//     },
//     createGeneralColumn("name", "toolPassportName", t),
//     createGeneralColumn("typeOfToolName", "typeOfTool", t),
//     createGeneralColumn("subcategoryName", "subcategory", t),
//     createGeneralColumn("categoryName", "category", t),
//     createGeneralColumn("panelName", "panel", t),
//     createActiveColumn(t),

//     {

//       field: 'details',
//       headerName: t("toolPassport"),
//       width: 120,
//       headerClassName: 'tableHeaderClassName',
//       cellClassName: 'tableCellClassName',
//       headerAlign: "center",
//       align: "center",
//       renderHeader: (params) => (
//         <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
//       ),
//       renderCell: (params) => (
//         <div
//           onClick={() => handleOpenModal(params.row)}
//           style={{ cursor: "pointer" }}
//         >
//           <i className="bi bi-eye text-danger">{" " + t("viewToolPassport")}</i>

//         </div>



//       )
//     },
//     createActionColumn(t, "/ToolPassport/Edit", remove, 80),
//     // eslint-disable-next-line
//   ].filter(Boolean), [t, remove]);

//   useEffect(() => {
//     setColumns(cols);
//     // eslint-disable-next-line
//   }, [t]);


//   return (
//     <>
//       <ListLayout
//         title={t("toolPassportListTitle")}
//         searchQuery={searchQuery}
//         onSearchChange={handleSearchChange}
//         searchPlaceholder={t("searchPlaceholder")}
//         addButtonLink="/ToolPassport/Add"
//         addButtonTitle={t("add")}
//       >
//         <DataTable
//           rows={filteredData}
//           columns={columns}
//           pageSize={pageSize}
//           setPageSize={setPageSize}
//           localeTextsMap={localizedTextsMap}
//         />
//       </ListLayout>
//       {hoveredLogo && (
//         <div style={{ position: 'fixed', top: `${cursorPosition.y + 20}px`, left: `${cursorPosition.x + 20}px`, zIndex: 1000, width: '150px', height: '150px', border: '1px solid #ccc', backgroundColor: '#fff', padding: '5px', }}        >
//           <img src={hoveredLogo} alt="Zoomed logo" style={{ width: '100%', height: '100%', objectFit: 'contain', }} />
//         </div>
//       )}
//       {/* Modal for displaying row details */}
//       {openModal && selectedRow && (
//         <Dialog open={openModal} onClose={handleCloseModal} fullWidth >
//           {/* <DialogTitle>{t("toolPassport")}</DialogTitle> */}
//           <DialogTitle
//             sx={{ display: 'flex', alignItems: 'center', marginBottom: '1px' }}>
//             {t("toolPassport")}
//             <IconButton onClick={handleCloseModal} sx={{ ml: 'auto' }}>
//               <CloseIcon />
//             </IconButton>
//           </DialogTitle>
//           {/* <hr /> */}
//           <DialogContent>
//             <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
//               <tbody>
//                 <tr>
//                   <td style={{ width: '33%', verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <strong>{t("toolPassportToolName")}</strong>
//                   </td>
//                   <td style={{ verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     {selectedRow.name}
//                   </td>
//                 </tr>
//                 <tr>
//                   <td style={{ width: '33%', verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <strong>{t("toolPassportLogo")}</strong>
//                   </td>
//                   <td style={{ verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <img src={selectedRow.logo ? `${process.env.REACT_APP_SERVER_URLForLogo}/${selectedRow.logo}` : `/assets/brand/default_img.png`} style={{ maxWidth: '100px', maxHeight: '100px' }} />
//                   </td>
//                 </tr>
//                 <tr>
//                   <td style={{ width: '33%', verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <strong>{t("typeOfTool")}</strong>
//                   </td>
//                   <td style={{ verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     {selectedRow.typeOfToolName}
//                   </td>
//                 </tr>
//                 <tr>
//                   <td style={{ width: '33%', verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <strong>{t("category")}</strong>
//                   </td>
//                   <td style={{ verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     {selectedRow.panelName} / {selectedRow.categoryName}
//                   </td>
//                 </tr>
//                 <tr>
//                   <td style={{ width: '33%', verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <strong>{t("toolPassportApplicationArea")}</strong>
//                   </td>
//                   <td style={{ verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     {selectedRow.subcategoryName}
//                   </td>
//                 </tr>
//                 <tr>
//                   <td style={{ width: '33%', verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <strong>{t("toolPassportFunctions")}</strong>
//                   </td>
//                   <td style={{ verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <span dangerouslySetInnerHTML={{ __html: selectedRow.function }} />
//                   </td>
//                 </tr>
//                 <tr>
//                   <td style={{ width: '33%', verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <strong>{t("toolPassportAbstractionLevel")}</strong>
//                   </td>
//                   <td style={{ verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <span dangerouslySetInnerHTML={{ __html: selectedRow.abstractionLevel }} />
//                   </td>
//                 </tr>
//                 <tr>
//                   <td style={{ width: '33%', verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <strong>{t("toolPassportIOConnection")}</strong>
//                   </td>
//                   <td style={{ verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <span dangerouslySetInnerHTML={{ __html: selectedRow.ioConnection }} />
//                   </td>
//                 </tr>
//                 <tr>
//                   <td style={{ width: '33%', verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <strong>{t("toolPassportVerificationTool")}</strong>
//                   </td>
//                   <td style={{ verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <span dangerouslySetInnerHTML={{ __html: selectedRow.verificationTool }} />
//                   </td>
//                 </tr>
//                 <tr>
//                   <td style={{ width: '33%', verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <strong>{t("toolPassportApplicationAreaOrEquivalenceCoverage")}</strong>
//                   </td>
//                   <td style={{ verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <span dangerouslySetInnerHTML={{ __html: selectedRow.applicationAreaOrEquivalenceCoverage }} />
//                   </td>
//                 </tr>
//                 <tr>
//                   <td style={{ width: '33%', verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <strong>{t("toolPassportQualitativeFeature")}</strong>
//                   </td>
//                   <td style={{ verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <span dangerouslySetInnerHTML={{ __html: selectedRow.qualitativeFeature }} />
//                   </td>
//                 </tr>
//                 <tr>
//                   <td style={{ width: '33%', verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <strong>{t("toolPassportQuantitativeFeature")}</strong>
//                   </td>
//                   <td style={{ verticalAlign: 'top', wordWrap: 'break-word', border: '1px solid #000', padding: '8px' }}>
//                     <span dangerouslySetInnerHTML={{ __html: selectedRow.quantitativeFeature }} />
//                   </td>
//                 </tr>
//               </tbody>
//             </table>
//           </DialogContent>
//           {/* <hr /> */}
//           <DialogActions>
//             <Button className='btn btn-primary' onClick={handleCloseModal}>{t("Close")}</Button>
//           </DialogActions>
//         </Dialog>
//       )}

//     </>
//   );
// };

// export default observer(List);